/**
 * 住所の欄（郵便番号 3桁＋4桁／都道府県／市区町村・番地／建物名）と、郵便番号→住所（025、GET /api/v1/address）。自社情報と顧客で共通。
 *   2026-09-19 射場さん（Board の形）：
 *   - 郵便番号は 3桁と4桁の2つの欄。3桁打つと自動で4桁の欄へ。「560-0081」「5600081」を貼っても2つに分けて入る
 *   - 都道府県は選ぶ欄（将来 県別の実績に使う。030 pref_code）。郵便番号で引くと都道府県も選ばれる
 *   - 7桁そろって「市区町村・番地」が空なら自動で引く。「住所検索」ボタンでも引ける（入っていても置き換える）
 *   - 引けたら「市区町村・番地」に「豊中市新千里北町」を入れてカーソルを末尾に置く（番地は続けて打つ。Board と同じ）
 *   - 1つの郵便番号に町域が複数あれば候補を並べて選ぶ
 * 親とやり取りする住所は今までどおり「都道府県から番地まで」の1行（value.address）。この部品の中で分けて見せ、つないで返す。
 * 「市区町村・番地」の欄に「大阪府…」と打ったら、欄を出たとき都道府県の欄へ移す
 *
 * single（#153、docs/62 §5・★2。自社情報の iOS 風）：郵便番号を1つの欄にして（3桁で「-」、7桁で「101-0047」。保存する値は数字7桁のまま）、
 *   iOS のまとまりの行（GroupList の中に置く <li>：郵便番号＋「住所検索」／都道府県（選ぶ行）／市区町村・番地／建物名）で描く。
 *   引く・候補・自動で引く・都道府県へ移すは分かれた欄と同じ。分かれた欄（顧客・見積・請求書）は今のまま
 */
import { ADDRESS_MSG, PREFECTURE_NAMES, joinAddress, normalizeZip, splitAddress, toHalfPhone, typeZip, type AddressLookup, type PostalCandidate } from "@genbaryoku/contract";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, ApiError, messageOf } from "../api.js";
import { GroupInputRow, GroupSelectRow } from "./GroupList.jsx";
import { PhoneField } from "./PhoneField.jsx";
import { Field, guardComposingEnter } from "./ui.jsx";

const PREF_OPTIONS = PREFECTURE_NAMES.map((p) => ({ value: p, label: p }));
const zipValueOf = (t: string) => t.replace(/-/g, "");

export type AddressValue = { postalCode: string; address: string; building: string };
type Zip = [string, string];

const digits = (s: string) => toHalfPhone(s).replace(/[^0-9]/g, "");
const splitZip = (v: string): Zip => {
  const d = digits(v);
  return [d.slice(0, 3), d.slice(3)];
};

/** 郵便番号の欄に打った（貼った）文字から、2つの欄の中身と次に置くカーソル */
export function nextZipParts(prev: Zip, i: 0 | 1, raw: string): { parts: Zip; focus: number; advanced: boolean } {
  const d = digits(raw);
  if (i === 0) {
    // うしろの欄が空（前から順に打っている）か、7桁まるごと入ったら、3桁で切って残りを4桁の欄へ
    if (prev[1] === "" || d.length >= 7) {
      if (d.length >= 3) return { parts: [d.slice(0, 3), d.slice(3, 7)], focus: 1, advanced: true };
      return { parts: [d, ""], focus: 0, advanced: false };
    }
    return { parts: [d.slice(0, 3), prev[1]], focus: 0, advanced: false };
  }
  if (d.length >= 7) return { parts: [d.slice(0, 3), d.slice(3, 7)], focus: 1, advanced: false };
  return { parts: [prev[0], d.slice(0, 4)], focus: 1, advanced: false };
}

export function AddressFields({
  value,
  onChange,
  ids,
  autoFocusAddress = false,
  single = false,
}: {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  /** 欄の id（ラベルと結ぶ）。zip は3桁の欄、address は「市区町村・番地」の欄。pref を省くと `${address}_pref` */
  ids: { zip: string; address: string; building: string; pref?: string };
  autoFocusAddress?: boolean;
  /** 郵便番号を1つの欄に・iOS のまとまりの行で描く（#153） */
  single?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | undefined>();
  const [candidates, setCandidates] = useState<PostalCandidate[] | null>(null);
  const zipRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const addrRef = useRef<HTMLInputElement>(null);
  const lastAuto = useRef<string>("");
  const prefId = ids.pref ?? `${ids.address}_pref`;
  const zip4LabelId = `${ids.zip}-label`;

  // 郵便番号の2つの欄（親の値は7桁の数字。打っている途中は3桁だけ等もある）
  const [zip, setZip] = useState<Zip>(() => splitZip(value.postalCode));
  const zipRef = useRef(zip);
  zipRef.current = zip;
  useEffect(() => {
    if (zipRef.current.join("") !== digits(value.postalCode)) setZip(splitZip(value.postalCode));
  }, [value.postalCode]);
  const zipFocus = useRef<number | null>(null);
  const zipJust = useRef(-1);
  const zipComposing = useRef(false);

  // 都道府県と「市区町村・番地」（親の値は1行）
  const [addr, setAddr] = useState(() => splitAddress(value.address));
  const addrStateRef = useRef(addr);
  addrStateRef.current = addr;
  useEffect(() => {
    const cur = addrStateRef.current;
    if (joinAddress(cur.pref, cur.rest) !== value.address) setAddr(splitAddress(value.address));
  }, [value.address]);
  const setAddress = (pref: string, rest: string, extra: Partial<AddressValue> = {}) => {
    setAddr({ pref, rest });
    onChange({ ...value, ...extra, address: joinAddress(pref, rest) });
  };

  const caretToEnd = useRef(false);
  const apply = (c: PostalCandidate) => {
    setAddress(c.pref, `${c.city}${c.town}`);
    setCandidates(null);
    setMsg(undefined);
    caretToEnd.current = true; // 番地を続けて打てるよう、「市区町村・番地」の欄の末尾へ（値が DOM に入った直後に）
  };
  useLayoutEffect(() => {
    if (caretToEnd.current) {
      caretToEnd.current = false;
      const el = addrRef.current;
      if (el) {
        el.focus();
        const n = el.value.length;
        el.setSelectionRange(n, n);
      }
    }
    if (zipFocus.current != null) {
      const el = zipRefs[zipFocus.current]?.current;
      zipFocus.current = null;
      if (el) {
        el.focus();
        const n = el.value.length;
        el.setSelectionRange(n, n);
      }
    }
  });

  const lookup = async (raw: string) => {
    const z = normalizeZip(raw);
    if (!z) {
      setMsg(ADDRESS_MSG.zipInvalid);
      return;
    }
    setBusy(true);
    setMsg(undefined);
    try {
      const r = await api<AddressLookup>("GET", `/address?zip=${z}`);
      if (r.candidates.length === 1) apply(r.candidates[0]!);
      else {
        setCandidates(r.candidates);
        setMsg(ADDRESS_MSG.pickOne);
      }
    } catch (e) {
      setCandidates(null);
      setMsg(e instanceof ApiError && e.code === "ADDRESS_NOT_FOUND" ? ADDRESS_MSG.notFound : messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  // 7桁そろって「市区町村・番地」が空なら自動で引く（同じ番号で2回は引かない）
  useEffect(() => {
    const z = normalizeZip(value.postalCode);
    if (!z || splitAddress(value.address).rest.trim() !== "" || lastAuto.current === z) return;
    lastAuto.current = z;
    void lookup(value.postalCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.postalCode]);

  const zipInput = (i: 0 | 1, raw: string) => {
    const r = nextZipParts(zipRef.current, i, raw);
    setZip(r.parts);
    setMsg(undefined);
    onChange({ ...value, postalCode: r.parts.join("") });
    if (r.focus !== i) zipFocus.current = r.focus;
    zipJust.current = r.advanced && r.focus !== i ? r.focus : -1;
  };
  const zipBox = (i: 0 | 1) => (
    <input
      ref={zipRefs[i]}
      id={i === 0 ? ids.zip : `${ids.zip}-2`}
      className={"inp " + (i === 0 ? "z3" : "z4")}
      type="text"
      inputMode="numeric"
      autoComplete={i === 0 ? "postal-code" : "off"}
      aria-label={i === 0 ? undefined : "4桁の欄"}
      aria-describedby={i === 0 ? `${ids.zip}-desc` : zip4LabelId}
      aria-invalid={msg === ADDRESS_MSG.zipInvalid || undefined}
      value={zip[i]}
      onKeyDown={(e) => {
        guardComposingEnter(e);
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        const empty = zip[i] === "";
        const swallow = zipJust.current === i && empty;
        if (e.key === "Enter") {
          e.preventDefault();
          void lookup(zip.join(""));
          return;
        }
        if (e.key === "-" || e.key === "－" || e.key === "ー") {
          e.preventDefault();
          zipJust.current = -1;
          if (!swallow && i === 0) {
            zipFocus.current = 1;
            setZip((z) => [...z] as Zip);
          }
          return;
        }
        if (e.key === "Tab" && !e.shiftKey && swallow) {
          e.preventDefault();
          zipJust.current = -1;
          return;
        }
        if (e.key !== "Tab" && e.key !== "Shift") zipJust.current = -1;
        if (e.key === "Backspace" && empty && i === 1) {
          e.preventDefault();
          zipFocus.current = 0;
          setZip((z) => [...z] as Zip);
        }
      }}
      onCompositionStart={() => {
        zipComposing.current = true;
      }}
      onCompositionEnd={(e) => {
        zipComposing.current = false;
        zipInput(i, e.currentTarget.value);
      }}
      onChange={(e) => {
        if (zipComposing.current) {
          const v = e.target.value;
          setZip((z) => (i === 0 ? [v, z[1]] : [z[0], v]));
        } else zipInput(i, e.target.value);
      }}
      onBlur={() => {
        if (zipJust.current === i) zipJust.current = -1;
      }}
    />
  );

  if (single) {
    const lookupBtn = (
      <button type="button" className="glink" disabled={busy} onClick={() => void lookup(value.postalCode)} aria-label="郵便番号から住所検索" data-testid="zip-lookup">
        {busy ? "検索中…" : "住所検索"}
      </button>
    );
    return (
      <>
        <GroupInputRow id={ids.zip} label="郵便番号" wide end={lookupBtn}>
          <PhoneField
            id={ids.zip}
            value={digits(value.postalCode)}
            format={typeZip}
            toValue={zipValueOf}
            inputMode="numeric"
            autoComplete="postal-code"
            invalid={msg === ADDRESS_MSG.zipInvalid}
            onEnter={() => void lookup(value.postalCode)}
            onChange={(z) => {
              setMsg(undefined);
              onChange({ ...value, postalCode: z });
            }}
          />
        </GroupInputRow>
        {msg || candidates ? (
          <li className="grow grow-set grow-msg">
            <div className="growin">
              {msg ? (
                <div className={"hint" + (msg === ADDRESS_MSG.pickOne ? "" : " shu")} role="status">
                  {msg}
                </div>
              ) : null}
              {candidates ? (
                <ul className="addr-cands" role="listbox" aria-label="町域の候補">
                  {candidates.map((c, i) => (
                    <li key={i}>
                      <button type="button" className="small" onClick={() => apply(c)}>
                        {c.pref}{c.city}{c.town}
                        {c.townNote ? <span className="muted">（{c.townNote}）</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        ) : null}
        <GroupSelectRow id={prefId} label="都道府県" value={addr.pref} placeholder="未選択" options={PREF_OPTIONS} onChange={(v) => setAddress(v, addr.rest)} />
        <GroupInputRow id={ids.address} label="市区町村・番地" wide>
          <input
            ref={addrRef}
            id={ids.address}
            className="giinp"
            autoComplete="address-line1"
            autoFocus={autoFocusAddress}
            value={addr.rest}
            onKeyDown={guardComposingEnter}
            onChange={(e) => setAddress(addr.pref, e.target.value)}
            onBlur={() => {
              const s = splitAddress(addr.rest);
              if (s.pref) setAddress(s.pref, s.rest);
            }}
          />
        </GroupInputRow>
        <GroupInputRow id={ids.building} label="建物名" wide>
          <input id={ids.building} className="giinp" autoComplete="address-line2" value={value.building} onKeyDown={guardComposingEnter} onChange={(e) => onChange({ ...value, building: e.target.value })} />
        </GroupInputRow>
      </>
    );
  }

  return (
    <div className="addr">
      <div className="row" style={{ alignItems: "flex-end", gap: 10 }}>
        <div className="field">
          <label id={zip4LabelId} htmlFor={ids.zip}>郵便番号</label>
          <span id={`${ids.zip}-desc`} className="sr">3桁と4桁の2つの欄です。3桁打つと自動で4桁の欄へ進みます</span>
          <div className="split zip">
            {zipBox(0)}
            <span className="sep" aria-hidden="true">-</span>
            {zipBox(1)}
          </div>
        </div>
        <button type="button" className="small" style={{ minHeight: 52 }} disabled={busy} onClick={() => void lookup(zip.join(""))} aria-label="郵便番号から住所検索">
          {busy ? "検索中…" : "住所検索"}
        </button>
      </div>
      {msg ? (
        <div className={"hint" + (msg === ADDRESS_MSG.pickOne ? "" : " shu")} role="status" style={{ marginTop: 4 }}>
          {msg}
        </div>
      ) : null}
      {candidates ? (
        <ul className="addr-cands" role="listbox" aria-label="町域の候補">
          {candidates.map((c, i) => (
            <li key={i}>
              <button type="button" className="small" onClick={() => apply(c)}>
                {c.pref}{c.city}{c.town}
                {c.townNote ? <span className="muted">（{c.townNote}）</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="row" style={{ marginTop: 10, alignItems: "flex-end" }}>
        <Field id={prefId} label="都道府県" style={{ flex: "0 0 auto" }}>
          <select id={prefId} className="inp pref" value={addr.pref} onChange={(e) => setAddress(e.target.value, addr.rest)}>
            <option value="">（選ぶ）</option>
            {PREFECTURE_NAMES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field id={ids.address} label="市区町村・番地" style={{ flex: "1 1 280px" }}>
          <input
            ref={addrRef}
            id={ids.address}
            className="inp"
            autoComplete="address-line1"
            autoFocus={autoFocusAddress}
            value={addr.rest}
            onKeyDown={guardComposingEnter}
            onChange={(e) => setAddress(addr.pref, e.target.value)}
            onBlur={() => {
              // 「大阪府豊中市…」と都道府県から打ったら、都道府県の欄へ移す
              const s = splitAddress(addr.rest);
              if (s.pref) setAddress(s.pref, s.rest);
            }}
          />
        </Field>
      </div>
      <Field id={ids.building} label="建物名" style={{ marginTop: 10 }}>
        <input id={ids.building} className="inp" autoComplete="address-line2" value={value.building} onKeyDown={guardComposingEnter} onChange={(e) => onChange({ ...value, building: e.target.value })} />
      </Field>
    </div>
  );
}
